import { useState, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@state/hooks'
import { createConversation, loadConversations, hydrateConversation } from '@state/slices/conversationsSlice'
import { setGraphContext, clearExploration, clearViewport } from '@state/slices/graphSlice'
import { clearDocument } from '@state/slices/documentSlice'
import { setCompaniesFromBackend, setActiveCompany } from '@state/slices/companiesSlice'
import { omapApi } from '@services/omapApi'
import { getNumericCompanyId } from '@utils/companyUtils'
import { showErrorToast } from '../utils/toastApi'
import { TabNavigationContext } from '@contexts/TabNavigationContext'
import type { CenterTab } from '@contexts/TabNavigationContext'
import CompanySelector from './sidebar/CompanySelector'
import ConversationList from './sidebar/ConversationList'
import ChatPanel from './chat/ChatPanel'
import StrategyWorkspaceTab from './tabs/StrategyWorkspaceTab'
import AnalysisReportTab from './tabs/AnalysisReportTab'
import BusinessGraphTab from './tabs/BusinessGraphTab'
import OracleInsightsTab from './tabs/OracleInsightsTab'
import DocumentTab from './tabs/DocumentTab'
import ExecutionTab from './tabs/ExecutionTab'
import ReviewTab from './tabs/ReviewTab'
import StrategyCanvas from './canvas/StrategyCanvas'
import AgencyConsoleTab from './tabs/AgencyConsoleTab'
import VigilDashboardTab from './tabs/VigilDashboardTab'
import DossierViewerTab from './tabs/DossierViewerTab'
import ThemeToggle from './shared/ThemeToggle'
import { HelpTrigger } from './help/HelpPanel'
import SampleBanner from './sample/SampleBanner'
import useCausalInsightsAutoSwitch from '@components/oracle/useCausalInsightsAutoSwitch'
import './MainLayout_v2.css'

function CausalInsightsAutoSwitchHost() {
  useCausalInsightsAutoSwitch()
  return null
}

export default function MainLayout() {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const userId: number = (user?.id as number) || 1
  const { conversations, activeConversationId } = useAppSelector(state => state.conversations)
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const [activeCenterTab, setActiveCenterTab] = useState<CenterTab>('workspace')
  const [leftPanelWidth, setLeftPanelWidth] = useState(250)
  const [rightPanelWidth, setRightPanelWidth] = useState(400)
  const [conversationsLoaded, setConversationsLoaded] = useState(false)
  const [lastUserTabChangeAt, setLastUserTabChangeAt] = useState<number | null>(null)

  // Handle tab navigation from chat links — user-initiated, stamps timestamp
  const handleTabNavigation = (tabId: CenterTab) => {
    setActiveCenterTab(tabId)
    setLastUserTabChangeAt(Date.now())

    // Smooth scroll to top of tab content
    setTimeout(() => {
      const tabContent = document.querySelector('.center-tab-content')
      if (tabContent) {
        tabContent.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 100)
  }

  // Programmatic navigation (auto-switch) — does NOT stamp timestamp
  const handleTabNavigationSilent = (tabId: CenterTab) => {
    setActiveCenterTab(tabId)
    setTimeout(() => {
      const tabContent = document.querySelector('.center-tab-content')
      if (tabContent) {
        tabContent.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 100)
  }

  // Fetch companies from backend on first load.
  // useRef guard prevents React StrictMode double-mount from triggering two fetches.
  // On success: populates Redux slice via setCompaniesFromBackend (maps snake_case → camelCase,
  // including isSample/isPublic), then auto-selects a company:
  //   - prefer a user-owned (non-sample) company if one exists
  //   - otherwise fall back to the sample company
  // On failure: log and leave state empty — no blank-company fallback.
  const companyInitRef = useRef(false)
  useEffect(() => {
    if (companyInitRef.current) return
    companyInitRef.current = true

    const loadCompanies = async () => {
      try {
        const result = await omapApi.getPortfolioCompanies(userId)
        const backendCompanies: any[] = result?.companies || []

        // Populate the slice (snake_case → camelCase, including isSample/isPublic).
        dispatch(setCompaniesFromBackend(backendCompanies))

        // Pick active company from the raw backend payload (Redux update is not yet
        // reflected in the closure — we read from the payload directly).
        if (!activeCompanyId) {
          const owned = backendCompanies.find((c: any) => !c.is_sample)
          const sample = backendCompanies.find((c: any) => c.is_sample)
          const target = owned || sample
          if (target) {
            const targetId = typeof target.id === 'string' ? target.id : `company_${target.id}`
            dispatch(setActiveCompany(targetId))
          }
        }
      } catch (err) {
        console.error('[MainLayout_v2] Failed to load companies from backend:', err)
        // Do not fall back to blank-company creation — an empty state is safer than
        // polluting the database with throwaway records on every auth failure.
        showErrorToast(
          'We could not load your companies just now. Please refresh the page; if it keeps happening, contact support.',
        )
      }
    }

    loadCompanies()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when company changes so conversations are re-fetched and tabs clear stale data
  useEffect(() => {
    setConversationsLoaded(false)
    dispatch(clearExploration())
    dispatch(clearViewport())
    dispatch(clearDocument())
  }, [activeCompanyId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load conversations from backend when company is selected
  useEffect(() => {
    if (!activeCompanyId) return

    const loadConversationsFromBackend = async () => {
      try {
        const numericCompanyId = getNumericCompanyId(activeCompanyId)
        console.log('[Persistence] Loading conversations for company:', activeCompanyId, '(numeric:', numericCompanyId, ')')
        const response = await omapApi.fetchConversations(numericCompanyId)

        if (response.status === 'success' && response.conversations) {
          console.log('[Persistence] Loaded conversations:', response.conversations.length)

          if (response.conversations.length > 0) {
            // Load conversation list
            dispatch(loadConversations(response.conversations))
            setConversationsLoaded(true)
          } else {
            // No conversations exist - create initial conversation
            console.log('[Persistence] No conversations found, creating initial conversation')
            dispatch(createConversation({ title: 'Getting Started', companyId: activeCompanyId }))
            setConversationsLoaded(true)
          }
        }
      } catch (error) {
        console.error('[Persistence] Failed to load conversations:', error)
        showErrorToast(
          'We could not load your saved conversations. Your history is safe; please refresh to try again.',
        )
        // Fallback: create initial conversation on error
        if (conversations.filter(c => c.companyId === activeCompanyId).length === 0) {
          dispatch(createConversation({ title: 'Getting Started', companyId: activeCompanyId }))
        }
        setConversationsLoaded(true)
      }
    }

    if (!conversationsLoaded) {
      loadConversationsFromBackend()
    }
  }, [activeCompanyId, conversationsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate active conversation with full data (messages, workspace)
  useEffect(() => {
    if (!activeConversationId || !conversationsLoaded) return

    const hydrateActiveConversation = async () => {
      try {
        const activeConv = conversations.find(c => c.id === activeConversationId)

        // Only hydrate if messages haven't been loaded yet
        if (!activeConv || !activeConv.messages || activeConv.messages.length === 0) {
          console.log('[Persistence] Hydrating conversation:', activeConversationId)
          const response = await omapApi.fetchConversation(activeConversationId)

          if (response.status === 'success' && response.conversation) {
            console.log('[Persistence] Hydrated conversation with', response.conversation.messages?.length || 0, 'messages')
            // Jan 2026: Detailed debug logging for Analysis Report persistence (Issue D investigation)
            const reportData = response.conversation.analysisReportData
            console.log('[Persistence] Analysis Report data:', reportData ? 'PRESENT' : 'NULL')
            if (reportData) {
              console.log('[Persistence] Analysis Report contents:', {
                hasCoverage: !!reportData.coverageAttestation,
                coverageLength: typeof reportData.coverageAttestation === 'string' ? reportData.coverageAttestation.length : 0,
                hasSynthesis: !!reportData.strategicSynthesis,
                synthesisKeys: reportData.strategicSynthesis ? Object.keys(reportData.strategicSynthesis) : [],
                synthesisPillars: reportData.strategicSynthesis?.pillar_derivation?.length || 0
              })
            }
            dispatch(hydrateConversation(response.conversation))

            // Restore graph context for Business Graph Tab (if present in hydrated data)
            // Validate graphContext is a proper object before dispatching
            const graphContext = response.conversation.strategyWorkspaceData?.graphContext
            if (graphContext && typeof graphContext === 'object' && !Array.isArray(graphContext)) {
              console.log('[Persistence] Restoring graph context:', graphContext.anchor_ids?.length || 0, 'anchors,', graphContext.explored_ids?.length || 0, 'explored')
              dispatch(setGraphContext(graphContext))
            }

          }
        } else {
          // Conversation already hydrated - restore graph context from existing data when switching conversations
          // Validate graphContext is a proper object before dispatching
          const graphContext = activeConv.strategyWorkspaceData?.graphContext
          if (graphContext && typeof graphContext === 'object' && !Array.isArray(graphContext)) {
            console.log('[Persistence] Restoring graph context from cached conversation:', graphContext.anchor_ids?.length || 0, 'anchors,', graphContext.explored_ids?.length || 0, 'explored')
            dispatch(setGraphContext(graphContext))
          }

        }
      } catch (error) {
        console.error('[Persistence] Failed to hydrate conversation:', error)
      }
    }

    hydrateActiveConversation()
  }, [activeConversationId, conversationsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TabNavigationContext.Provider value={{
      navigateToTab: handleTabNavigation,
      navigateToTabSilent: handleTabNavigationSilent,
      lastUserTabChangeAt,
    }}>
      <CausalInsightsAutoSwitchHost />
      <div className="main-layout-v2">
        <SampleBanner />
        {/* Top Bar */}
        <div className="top-bar">
          <div className="top-bar-left">
            <h1 className="app-title">Causeway</h1>
          </div>
          <div className="top-bar-right">
            <button
              className="data-manager-btn"
              title="Data Manager"
              onClick={() => window.open('/data-manager', '_blank')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1C4.7 1 2 2.1 2 3.5v9C2 13.9 4.7 15 8 15s6-1.1 6-2.5v-9C14 2.1 11.3 1 8 1zm0 1.5c2.8 0 4.5.9 4.5 1.5S10.8 5.5 8 5.5 3.5 4.6 3.5 4 5.2 2.5 8 2.5zM3.5 12.5v-2c1 .7 2.7 1 4.5 1s3.5-.3 4.5-1v2c0 .6-1.7 1.5-4.5 1.5S3.5 13.1 3.5 12.5zm0-4v-2c1 .7 2.7 1 4.5 1s3.5-.3 4.5-1v2c0 .6-1.7 1.5-4.5 1.5S3.5 9.1 3.5 8.5z"/>
              </svg>
            </button>
            <button
              className="data-manager-btn"
              title="Portfolio Dashboard"
              onClick={() => window.open('/portfolio', '_blank')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 3h4v4H1V3zm5 0h4v4H6V3zm5 0h4v4h-4V3zM1 9h4v4H1V9zm5 0h4v4H6V9zm5 0h4v4h-4V9z"/>
              </svg>
            </button>
            <button
              className="data-manager-btn"
              title="User Guide"
              onClick={() => window.open('/help', '_blank')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.5a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11zM7.25 11h1.5v-1.5h-1.5V11zm.27-3h.96c.06-.5.22-.86.6-1.25.5-.5.93-1.05.93-1.8 0-1.1-.86-1.95-2.18-1.95-1.16 0-2.07.7-2.22 1.85h1.36c.1-.55.42-.85.88-.85.5 0 .8.33.8.77 0 .44-.28.73-.68 1.12-.48.47-.72.88-.72 1.58v.28h.27z"/>
              </svg>
            </button>
            <ThemeToggle />
            <button
              className="data-manager-btn"
              title="Settings"
              onClick={() => window.open(`/settings?company=${activeCompanyId || 'company_1'}`, '_blank')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm0-1.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                <path d="M13.5 7h-.73a5.5 5.5 0 0 0-.47-1.13l.52-.52a.5.5 0 0 0 0-.7l-.71-.71a.5.5 0 0 0-.7 0l-.52.52A5.5 5.5 0 0 0 9.76 4V3.27a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5V4c-.4.1-.78.25-1.13.47l-.52-.52a.5.5 0 0 0-.7 0l-.71.71a.5.5 0 0 0 0 .7l.52.52A5.5 5.5 0 0 0 4.74 7H4a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h.73c.1.4.26.78.47 1.13l-.52.52a.5.5 0 0 0 0 .7l.71.71a.5.5 0 0 0 .7 0l.52-.52c.35.21.73.37 1.13.47v.73a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-.73c.4-.1.78-.26 1.13-.47l.52.52a.5.5 0 0 0 .7 0l.71-.71a.5.5 0 0 0 0-.7l-.52-.52c.21-.35.37-.73.47-1.13h.73a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/>
              </svg>
            </button>
            <span className="user-info">Welcome, {user?.username}</span>
          </div>
        </div>

      {/* Main Content - 3 Panel Layout */}
      <div className="content-panels">
        {/* Left Panel - Company Selector + Conversation List */}
        <div
          className="left-panel"
          style={{ width: `${leftPanelWidth}px` }}
        >
          <CompanySelector />
          <ConversationList />
        </div>

        {/* Left Resize Handle */}
        <div
          className="resize-handle left-resize"
          onMouseDown={(e) => {
            e.preventDefault()
            const startX = e.clientX
            const startWidth = leftPanelWidth

            const handleMouseMove = (e: MouseEvent) => {
              const delta = e.clientX - startX
              const newWidth = Math.max(200, Math.min(400, startWidth + delta))
              setLeftPanelWidth(newWidth)
            }

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
          }}
        />

        {/* Center Panel - Dual Tab Content */}
        <div className="center-panel">
          {/* Tab Bar — WS3 grouped: ANALYZE / UNDERSTAND / ACT / REVIEW */}
          <div className="center-tab-bar-grouped">
            <div className="tab-group grp-analyze">
              <div className="tab-group-label">ANALYZE</div>
              <div className="tab-group-tabs">
                <button
                  className={`center-tab ${activeCenterTab === 'workspace' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('workspace')}
                >
                  Strategy Workspace
                </button>
              </div>
            </div>
            <div className="tab-group grp-understand">
              <div className="tab-group-label">UNDERSTAND</div>
              <div className="tab-group-tabs">
                <button
                  className={`center-tab ${activeCenterTab === 'report' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('report')}
                >
                  Analysis Report
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'oracle' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('oracle')}
                >
                  Causal Insights
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'graph' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('graph')}
                >
                  Business Graph
                </button>
              </div>
            </div>
            <div className="tab-group grp-act">
              <div className="tab-group-label">ACT</div>
              <div className="tab-group-tabs">
                <button
                  className={`center-tab ${activeCenterTab === 'document' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('document')}
                >
                  Document
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'execution' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('execution')}
                >
                  Execution
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'canvas' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('canvas')}
                >
                  Canvas
                </button>
              </div>
            </div>
            <div className="tab-group grp-review">
              <div className="tab-group-label">REVIEW</div>
              <div className="tab-group-tabs">
                <button
                  className={`center-tab ${activeCenterTab === 'review' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('review')}
                >
                  Review
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'vigil' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('vigil')}
                >
                  Vigil
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'dossier' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('dossier')}
                >
                  Dossier
                </button>
                <button
                  className={`center-tab ${activeCenterTab === 'agency' ? 'active' : ''}`}
                  onClick={() => setActiveCenterTab('agency')}
                >
                  Agency
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="center-tab-content">
            {(() => {
              const tabHelpMap: Record<string, string> = {
                workspace: 'strategy-workspace',
                report: 'strategy-workspace',
                graph: 'business-graph',
                oracle: 'oracle-insights',
                document: 'document',
                execution: 'execution',
                review: 'review',
                canvas: 'canvas',
                vigil: 'vigil',
                dossier: 'dossier',
                agency: 'agency',
              }
              const section = tabHelpMap[activeCenterTab]
              return section ? <HelpTrigger section={section} /> : null
            })()}
            {activeCenterTab === 'workspace' && <StrategyWorkspaceTab />}
            {activeCenterTab === 'report' && <AnalysisReportTab />}
            {activeCenterTab === 'graph' && <BusinessGraphTab />}
            {activeCenterTab === 'oracle' && <OracleInsightsTab />}
            {activeCenterTab === 'document' && <DocumentTab />}
            {activeCenterTab === 'execution' && <ExecutionTab />}
            {activeCenterTab === 'review' && <ReviewTab />}
            {activeCenterTab === 'canvas' && <StrategyCanvas />}
            {activeCenterTab === 'vigil' && <VigilDashboardTab />}
            {activeCenterTab === 'dossier' && <DossierViewerTab />}
            {activeCenterTab === 'agency' && <AgencyConsoleTab />}
          </div>
        </div>

        {/* Right Resize Handle */}
        <div
          className="resize-handle right-resize"
          onMouseDown={(e) => {
            e.preventDefault()
            const startX = e.clientX
            const startWidth = rightPanelWidth

            const handleMouseMove = (e: MouseEvent) => {
              const delta = startX - e.clientX // Reverse direction for right panel
              const newWidth = Math.max(300, Math.min(600, startWidth + delta))
              setRightPanelWidth(newWidth)
            }

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
          }}
        />

        {/* Right Panel - Chat */}
        <div
          className="right-panel"
          style={{ width: `${rightPanelWidth}px` }}
        >
          <ChatPanel />
        </div>
      </div>
    </div>
    </TabNavigationContext.Provider>
  )
}
